import dayjs from "dayjs";

const GENERIC_ERROR = "Request failed. Please try again.";

const isBlank = (value) => value === null || value === undefined || value === "";

export const validatePreClearForm = ({ security, pricingDate, filingDate }) => {
  const errors = {};
  if (!security || !String(security).trim()) {
    errors.security = "Security is required.";
  }
  const pricing = isBlank(pricingDate) ? null : dayjs(pricingDate);
  const filing = isBlank(filingDate) ? null : dayjs(filingDate);
  if (!pricing) {
    errors.pricingDate = "Pricing date is required.";
  } else if (!pricing.isValid()) {
    errors.pricingDate = "Pricing date is not a valid date.";
  }
  if (filing && !filing.isValid()) {
    errors.filingDate = "Filing date is not a valid date.";
  } else if (filing && pricing && pricing.isValid() && filing.isAfter(pricing, "day")) {
    errors.filingDate = "Filing date cannot be after the pricing date.";
  }
  return errors;
};

export const formatWindow = (start, end) => `${start} to ${end}`;

// Only a real boolean is trusted; anything else is never shown as clear.
export const flagLabel = (flagged) => {
  if (flagged === true) return "Flagged for compliance review";
  if (flagged === false) return "No sales or short sales found";
  return "Result unavailable";
};

const relatedVerdict = (relatedFlagged) => {
  if (relatedFlagged === true) return "Sales or short sales found";
  if (relatedFlagged === false) return "No sales or short sales found";
  return "Result unavailable";
};

export const relatedFlagLabel = (relatedFlagged) =>
  `Related instruments: ${relatedVerdict(relatedFlagged).toLowerCase()}`;

// Colour role for a verdict: flagged (amber), clean (green), anything else unavailable (grey).
export const verdictTone = (flagged) => {
  if (flagged === true) return "flagged";
  if (flagged === false) return "clean";
  return "unavailable";
};

export const summarizeCheck = (result) => {
  const snapshot = result?.snapshot || {};
  return {
    shortTrades: (snapshot.trades || []).length,
    relatedInstruments: (snapshot.related || []).length,
    positions: (snapshot.positions || []).length,
  };
};

const MARKERS = [
  ["has_short_sales", "Short sales"],
  ["has_long_sales", "Sales of long positions"],
  ["net_short_today", "Net short today"],
  ["unresolved_security", "Unresolved security"],
];

// Reads a marker from a record or check result (top level first, then its snapshot).
// Only a real boolean is trusted; old-format data without the key is "Unavailable", never "No".
const markerRaw = (source, key) =>
  typeof source?.[key] === "boolean" ? source[key] : source?.snapshot?.[key];

export const markerBadges = (source) =>
  MARKERS.map(([key, label]) => {
    const raw = markerRaw(source, key);
    return {
      key,
      label,
      value: raw === true ? "Yes" : raw === false ? "No" : "Unavailable",
    };
  });

export const reasonLines = (reasons) =>
  Array.isArray(reasons) ? reasons.filter((r) => typeof r === "string" && r !== "") : [];

// Net position total and per-symbol rows exactly as the server computed them (strings, no float math).
export const netPositionSummary = (snapshot) => {
  const total = typeof snapshot?.net_position === "string" ? snapshot.net_position : null;
  const list = Array.isArray(snapshot?.net_positions) ? snapshot.net_positions : null;
  if (total === null || list === null) return { total: "unavailable", rows: null };
  return {
    total,
    rows: list.map((p) => ({
      symbol: p?.symbol || "Unavailable",
      netQuantity:
        p?.net_quantity === null || p?.net_quantity === undefined || p?.net_quantity === ""
          ? "Unavailable"
          : p.net_quantity,
      securityType: p?.security_type || "Unavailable",
      inNet: p?.in_net_quantity !== false,
    })),
  };
};

// The typed security matched no trade or position, so "no short sales" means "no data".
export const isUnresolved = (result) => result?.snapshot?.unresolved_security === true;

// Attest only on a real boolean result (never on RESULT UNAVAILABLE).
export const canAttestResult = ({ result, accepted, attesting, record }) =>
  Boolean(result) && typeof result.flagged === "boolean" && accepted && !attesting && !record;

export const errorMessage = (error) => {
  const data = error?.response?.data;
  if (data?.detail) return data.detail;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const parts = Object.entries(data).map(
      ([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(" ") : String(msgs)}`,
    );
    if (parts.length > 0) return parts.join("; ");
  }
  return GENERIC_ERROR;
};

export const resultsDiffer = (reviewed, stored) => {
  if (!reviewed || !stored) return true;
  return (
    reviewed.flagged !== stored.flagged ||
    reviewed.related_flagged !== stored.related_flagged ||
    JSON.stringify(reviewed.flag_reasons || []) !== JSON.stringify(stored.flag_reasons || []) ||
    // Missing (old-format) is distinct from false; both missing is equal.
    MARKERS.some(([key]) => markerRaw(reviewed, key) !== markerRaw(stored, key)) ||
    reviewed.snapshot?.match_mode !== stored.snapshot?.match_mode
  );
};

export const capRows = (rows, max) => ({
  shown: rows.slice(0, max),
  hidden: Math.max(0, rows.length - max),
});

export const signaturePrefix = (signature, length = 12) => {
  const s = String(signature || "");
  return s.length > length ? `${s.slice(0, length)}...` : s;
};

// Saves a fetched PDF blob as a file download.
export const savePdfBlob = (blob, id) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `regm-preclear-${id}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Type ahead helpers. Suggestions are hints only; the visible text is what gets checked.
export const MIN_SUGGEST_CHARS = 3;
export const MAX_SUGGESTIONS = 20;

export const shouldSuggest = (text) =>
  typeof text === "string" && text.trim().length >= MIN_SUGGEST_CHARS;

const present = (v) => (v === null || v === undefined ? "" : String(v).trim());

export const suggestionLabel = (row) =>
  [row?.symbol, row?.ticker, row?.name, row?.security_type, row?.isin]
    .map(present)
    .filter(Boolean)
    .join(" | ");

// The text placed in the box when a suggestion is chosen.
export const suggestionValue = (row) =>
  present(row?.symbol) || present(row?.ticker) || present(row?.isin);

export const prepareSuggestions = (rows, max = MAX_SUGGESTIONS) => {
  if (!Array.isArray(rows)) return [];
  const seen = new Set();
  const out = [];
  rows.forEach((row) => {
    const label = suggestionLabel(row);
    const value = suggestionValue(row);
    if (!label || !value || seen.has(label)) return;
    seen.add(label);
    out.push({ label, value });
  });
  return out.slice(0, max);
};

// Prefix search text: an optional single trailing * with at least 2 literal characters before it.
// Mirrors the server rules for immediate feedback; the server stays authoritative.
export const WILDCARD_MIN_LITERAL = 2;

export const wildcardProblem = (text) => {
  const t = typeof text === "string" ? text.trim() : "";
  if (!t.includes("*")) return null;
  const stars = t.split("*").length - 1;
  if (stars > 1 || !t.endsWith("*")) {
    return `Invalid search text '${t}': use * only once, as the last character (for example CRG*).`;
  }
  if (t.slice(0, -1).trim().length < WILDCARD_MIN_LITERAL) {
    return `Search text '${t}' is too short for a prefix search: type at least ${WILDCARD_MIN_LITERAL} characters before the *.`;
  }
  return null;
};

// Only a real boolean true marks a prefix search; old-format data shows nothing extra.
export const isWildcardResult = (source) => source?.snapshot?.wildcard === true;

export const wildcardSummary = (source) => {
  if (!isWildcardResult(source)) return null;
  const snapshot = source.snapshot;
  const count = Array.isArray(snapshot.resolved_symbols) ? snapshot.resolved_symbols.length : 0;
  const pattern = typeof snapshot.wildcard_pattern === "string" ? `${snapshot.wildcard_pattern} ` : "";
  return `Prefix search: ${pattern}matched ${count} symbol${count === 1 ? "" : "s"}`;
};

// Only the strict string "exact" marks an exact match; old records and anything else show nothing.
export const matchModeSummary = (source) =>
  source?.snapshot?.match_mode === "exact" ? "Exact match" : null;

// Pricing date starts as today's local date; today is injectable for tests.
export const defaultPricingDate = (today = dayjs()) => today.startOf("day");

// Same conditions as the Check button, so Enter in the security box cannot bypass them.
export const canSubmitCheck = ({ text, checking, attesting }) =>
  typeof text === "string" &&
  text.trim() !== "" &&
  wildcardProblem(text) === null &&
  !checking &&
  !attesting;

// Exact flag for a submit: an explicit mode (Enter on a highlighted suggestion) wins;
// otherwise it follows the current chosen state, exactly as the Check button does.
export const submitExact = (explicit, exactChosen) =>
  explicit === undefined ? exactChosen === true : explicit === true;

// Decision rail state. "ready" is the only attestable state and matches canAttestResult exactly;
// no result (typing cleared it) or a check in flight hides the rail.
export const railState = ({ result, accepted, attesting, record, checking } = {}) => {
  if (!result || checking === true) return "hidden";
  if (record) return "confirmed";
  if (attesting === true) return "attesting";
  if (typeof result.flagged !== "boolean") return "unavailable";
  return accepted === true ? "ready" : "review";
};

// Rail markers: hide only a strict "No"; "Yes" and "Unavailable" (old or partial data) stay visible.
export const railMarkerBadges = (source) => markerBadges(source).filter((b) => b.value !== "No");

// Rail verdict. An unresolved security is never a green all-clear: nothing could be checked.
export const railVerdict = (result) => {
  const tone = verdictTone(result?.flagged);
  const unresolved = isUnresolved(result);
  return {
    label: flagLabel(result?.flagged),
    tone: unresolved && tone === "clean" ? "neutral" : tone,
    note: unresolved ? "Security not matched to any trade or position; nothing could be checked" : null,
  };
};

// "Net position" for one matched symbol; the total wording when the net spans several counted symbols.
export const netTotalLabel = (result) => {
  const snapshot = result?.snapshot || {};
  const resolved = Array.isArray(snapshot.resolved_symbols) ? snapshot.resolved_symbols.length : 0;
  const counted = new Set(
    (Array.isArray(snapshot.net_positions) ? snapshot.net_positions : [])
      .filter((p) => p?.in_net_quantity !== false)
      .map((p) => String(p?.symbol ?? "").trim().toLowerCase()),
  ).size;
  return resolved > 1 || counted > 1
    ? "Total net position across matched symbols (excluding swaps)"
    : "Net position";
};

// Same grouping key as the server: lower(trim(symbol)) plus whether the row counts in the net.
const groupKey = (symbol, inNet) =>
  `${String(symbol ?? "").trim().toLowerCase()}|${inNet ? "net" : "swap"}`;

// Positions grouped under the server's per-symbol net (net string shown verbatim, never recomputed).
// Rows without a matching net row (or old snapshots without nets) are still listed, with net null.
// The cap counts position rows; a group past the cap is hidden whole and counted.
export const groupPositions = (snapshot, max) => {
  const positions = Array.isArray(snapshot?.positions) ? snapshot.positions : [];
  const net = netPositionSummary(snapshot);
  const groups = [];
  const byKey = new Map();
  if (net.rows !== null) {
    net.rows.forEach((row, i) => {
      const group = { key: `n${i}`, rows: [], net: row };
      const key = groupKey(snapshot.net_positions[i]?.symbol, row.inNet);
      if (!byKey.has(key)) byKey.set(key, group);
      groups.push(group);
    });
  }
  positions.forEach((p) => {
    const inNet = p?.in_net_quantity !== false;
    const key = groupKey(p?.symbol, inNet);
    if (!byKey.has(key)) {
      const group = { key: `p${groups.length}`, rows: [], net: null };
      byKey.set(key, group);
      groups.push(group);
    }
    byKey.get(key).rows.push({
      symbol: p?.symbol,
      strategy: p?.strategy_type,
      securityType: p?.security_type,
      realtimeQuantity: p?.realtime_quantity,
      netTradingQuantity: p?.net_trading_quantity,
      inNet,
    });
  });
  let used = 0;
  let hiddenRows = 0;
  let hiddenNets = 0;
  const shown = [];
  groups.forEach((group) => {
    if (used >= max) {
      hiddenRows += group.rows.length;
      if (group.net) hiddenNets += 1;
      return;
    }
    const rows = group.rows.slice(0, max - used);
    hiddenRows += group.rows.length - rows.length;
    used += rows.length;
    shown.push({ ...group, rows });
  });
  return { netAvailable: net.rows !== null, total: net.total, groups: shown, hiddenRows, hiddenNets };
};

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

// Evidence sections: trades and positions open when they have rows; related stays closed but its
// verdict and count are always in the summary line, so a closed section never hides a finding.
export const evidenceSections = (result) => {
  const counts = summarizeCheck(result);
  const net = netPositionSummary(result?.snapshot);
  const netShort = markerRaw(result, "net_short_today") === true;
  const label = netTotalLabel(result);
  let positionsTone = "neutral";
  if (netShort) positionsTone = "flagged";
  else if (net.rows === null) positionsTone = "unavailable";
  const positionsSummary =
    counts.positions === 0
      ? `No positions; ${label.charAt(0).toLowerCase()}${label.slice(1)} ${net.total}`
      : `${label} ${net.total}${netShort ? "; net short today" : ""}`;
  return {
    trades: {
      count: counts.shortTrades,
      empty: counts.shortTrades === 0,
      defaultExpanded: counts.shortTrades > 0,
      tone: counts.shortTrades > 0 ? "flagged" : "clean",
      summary: counts.shortTrades > 0 ? `${counts.shortTrades} in the window` : "None in the window",
    },
    positions: {
      count: counts.positions,
      empty: counts.positions === 0,
      defaultExpanded: counts.positions > 0,
      tone: positionsTone,
      summary: positionsSummary,
    },
    related: {
      count: counts.relatedInstruments,
      empty: counts.relatedInstruments === 0,
      defaultExpanded: false,
      tone: verdictTone(result?.related_flagged),
      summary: `${relatedVerdict(result?.related_flagged)}; ${plural(counts.relatedInstruments, "trade")}`,
    },
  };
};

// Field guidance lives in tooltips; a field's helper text is only ever its validation error.
export const fieldErrorText = (error) => (error ? error : undefined);
